/** What makes the application installable on a phone's home screen. */

export const MANIFEST_PATH = "/manifest.webmanifest";
export const SERVICE_WORKER_PATH = "/sw.js";
export const APPLE_TOUCH_ICON_PATH = "/icons/apple-touch-icon.png";

/**
 * The colour behind the status bar and the splash screen: the default
 * palette's background. It is a static tag because a launch paints it before
 * any script has read the visitor's palette.
 */
export const THEME_COLOR = "#0c1016";

/** The head tags an installable page carries, as TanStack `head` entries. */
export function installableHead(): {
  meta: { name: string; content: string }[];
  links: { rel: string; href: string }[];
} {
  return {
    meta: [
      { name: "theme-color", content: THEME_COLOR },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-title", content: "SyncAni" },
      // Opaque, so every page starts under the status bar rather than each
      // one padding itself by the top inset; black is the default palette's
      // background to within a shade, where `default` would be a white strip.
      { name: "apple-mobile-web-app-status-bar-style", content: "black" },
    ],
    links: [
      { rel: "manifest", href: MANIFEST_PATH },
      { rel: "apple-touch-icon", href: APPLE_TOUCH_ICON_PATH },
    ],
  };
}

/**
 * Registers the service worker, where there is one to register.
 *
 * Only a production build registers: a worker installed by the dev server
 * outlives it and would sit in front of the next one on the same port.
 * Returns whether a registration was made; a refusal is not an error worth
 * surfacing, since the app works the same without it.
 */
export async function registerServiceWorker(
  production: boolean,
  container: ServiceWorkerContainer | undefined,
): Promise<boolean> {
  if (!production || !container) return false;
  try {
    await container.register(SERVICE_WORKER_PATH, { scope: "/" });
    return true;
  } catch {
    return false;
  }
}
